import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, MapPin, Calendar, Clock, ExternalLink, User, Ticket, Globe } from 'lucide-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useStructuredData } from '../hooks/useStructuredData';
import { generateEventSchema } from '../utils/structuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventPage = ({ onBackToHome }) => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate structured data for the event
  const eventSchema = React.useMemo(() => {
    return event ? generateEventSchema(event) : null;
  }, [event]);

  // Apply structured data
  useStructuredData(eventSchema, 'event-schema');

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${eventId}`);
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event:', error);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  const getEventTypeBadgeColor = (type) => {
    const colors = {
      'market': 'bg-green-100 text-green-800',
      'festival': 'bg-purple-100 text-purple-800',
      'sale event': 'bg-red-100 text-red-800',
      'craft fair': 'bg-blue-100 text-blue-800',
      'conference': 'bg-orange-100 text-orange-800'
    };
    return colors[type?.toLowerCase()] || colors['market'];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!endDate || startDate === endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-charcoal mb-4">Event Not Found</h1>
          <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has ended.</p>
          <Button onClick={onBackToHome}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button 
            onClick={onBackToHome}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Events
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Event Info */}
          <div className="lg:col-span-2">
            <Card className="mb-6">
              {event.image_url && (
                <div className="aspect-video">
                  <img 
                    src={event.image_url} 
                    alt={event.title || event.name}
                    className="w-full h-full object-cover rounded-t-lg"
                  />
                </div>
              )}
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-bold text-charcoal mb-2">
                      {event.title || event.name}
                    </CardTitle>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={getEventTypeBadgeColor(event.event_type || event.type)}>
                        {(event.event_type || event.type)?.toUpperCase() || 'EVENT'}
                      </Badge>
                      {event.is_featured && (
                        <Badge className="bg-amber-100 text-amber-800">
                          ⭐ Featured
                        </Badge>
                      )}
                      {event.is_free || event.ticket_price === 'Free' ? (
                        <Badge className="bg-green-100 text-green-800">
                          FREE
                        </Badge>
                      ) : event.ticket_price && (
                        <Badge className="bg-blue-100 text-blue-800">
                          {event.ticket_price}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDateRange(event.date || event.start_date, event.end_date)}</span>
                      </div>
                      {event.time && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{event.time}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-charcoal mb-3">
                    About This Event
                  </h3>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {event.description}
                    </p>
                  </div>
                </div>

                {/* Event Highlights */}
                {event.highlights && event.highlights.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-charcoal mb-3">
                      Event Highlights
                    </h3>
                    <ul className="space-y-2">
                      {event.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-primary mt-2">•</span>
                          <span className="text-gray-700">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Learn More / Book Tickets */}
                <div className="bg-primary/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-charcoal mb-3">
                    {event.is_free || event.ticket_price === 'Free' ? 'Learn More' : 'Book Tickets'}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {event.is_free || event.ticket_price === 'Free' 
                      ? 'Get more information about this free event.'
                      : 'Reserve your spot at this event.'}
                  </p>
                  {event.website && (
                    <Button 
                      className="w-full sm:w-auto"
                      onClick={() => window.open(event.website, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {event.is_free || event.ticket_price === 'Free' ? 'Learn More' : 'Book Now'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Event Details Sidebar */}
          <div className="lg:col-span-1">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-charcoal mb-1">Date & Time</h4>
                  <p className="text-gray-600">
                    {formatDateRange(event.date || event.start_date, event.end_date)}
                  </p>
                  {event.time && (
                    <p className="text-gray-600 text-sm">{event.time}</p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-charcoal mb-1">Location</h4>
                  <p className="text-gray-600">{event.location}</p>
                  {event.address && (
                    <p className="text-gray-600 text-sm">{event.address}</p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-charcoal mb-1">Organizer</h4>
                  <p className="text-gray-600">{event.organizer}</p>
                </div>

                <div>
                  <h4 className="font-medium text-charcoal mb-1">Event Type</h4>
                  <Badge className={getEventTypeBadgeColor(event.event_type || event.type)}>
                    {(event.event_type || event.type)?.toUpperCase() || 'EVENT'}
                  </Badge>
                </div>

                {event.ticket_price && (
                  <div>
                    <h4 className="font-medium text-charcoal mb-1">Ticket Price</h4>
                    <p className="text-gray-600">{event.ticket_price}</p>
                  </div>
                )}

                {event.contact_info && (
                  <div>
                    <h4 className="font-medium text-charcoal mb-1">Contact</h4>
                    <p className="text-gray-600 text-sm">{event.contact_info}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="p-6 space-y-3">
                {event.website && (
                  <Button 
                    className="w-full"
                    onClick={() => window.open(event.website, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {event.is_free || event.ticket_price === 'Free' ? 'Learn More' : 'Book Tickets'}
                  </Button>
                )}

                {event.organizer_website && (
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open(event.organizer_website, '_blank')}
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Organizer Website
                  </Button>
                )}

                <p className="text-xs text-gray-500 text-center">
                  You'll be redirected to the event organizer's page
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPage;